/**
 * Traditional-Chinese (zh-Hant) conversion chokepoint.
 *
 * The MyCIO copy catalogs (translations.ts) already carry a Simplified-Chinese ('zh') variant. Rather than
 * maintain a second hand-authored Traditional catalog, we convert the Simplified content to Taiwan-standard
 * Traditional at the point each getter resolves it, via opencc-js (cn -> tw). One converter, applied wholesale,
 * so every MyCIO surface gets Traditional at once — and there is nothing extra to translate or keep in sync.
 *
 * Deterministic + cached: the catalogs are static module constants, so each source object is converted once
 * (WeakMap by identity). Graceful: if opencc is unavailable or a value isn't a CJK string, we return it as-is
 * (an honest fallback — never a crash, never English run through the Chinese converter).
 */
import { Converter } from 'opencc-js'

let _convert: ((s: string) => string) | null = null
function converter(): (s: string) => string {
  if (_convert) return _convert
  try {
    _convert = Converter({ from: 'cn', to: 'tw' }) // Simplified -> Taiwan-standard Traditional
  } catch {
    _convert = (s: string) => s // opencc missing/broken -> passthrough (degrade to Simplified, never crash)
  }
  return _convert
}

const CJK = /[㐀-鿿豈-﫿]/ // Han ideographs — skip conversion for pure-ASCII/Latin strings

export function toTraditional(s: string): string {
  if (!s || typeof s !== 'string' || !CJK.test(s)) return s
  try {
    return converter()(s)
  } catch {
    return s
  }
}

const _deepCache = new WeakMap<object, unknown>()

/** Deep-convert every string leaf (and every template's output) of a JSON-ish value to Traditional Chinese; caches per source object. */
export function toTraditionalDeep<T>(value: T): T {
  if (value == null) return value
  if (typeof value === 'string') return toTraditional(value) as unknown as T
  // A catalog template (`(n) => \`…\``) is authored in Simplified too: convert what it returns.
  if (typeof value === 'function') {
    const fn = value as unknown as (...args: unknown[]) => unknown
    return ((...args: unknown[]) => toTraditionalDeep(fn(...args))) as unknown as T
  }
  if (typeof value !== 'object') return value
  const hit = _deepCache.get(value as object)
  if (hit !== undefined) return hit as T
  let out: unknown
  if (Array.isArray(value)) {
    out = value.map((v) => toTraditionalDeep(v))
  } else {
    const o: Record<string, unknown> = {}
    for (const k of Object.keys(value as Record<string, unknown>)) {
      o[k] = toTraditionalDeep((value as Record<string, unknown>)[k])
    }
    out = o
  }
  _deepCache.set(value as object, out)
  return out as T
}
